import { FC, useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore, useWalletStore } from '@/store'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import {
  TradingPanel,
  PortfolioPanel,
  GameStatusPanel,
  MessagePanel,
  SituationPanel,

  OnboardingModal,
  shouldShowOnboarding,
  ShareCardPreview,

  PersistentPortfolioBar,
  DeadlineWarning,
  TradeHistoryPanel,
  PendingTradeModal,
  VoiceControls,
} from '@/components/game'
import { parseTradeIntent, isDirectTradeRequest, type TradeIntent } from '@/utils/tradeIntentParser'
import type { ShareCardData } from '@/services/shareCard'
import { generateGameRecapViaCloud, type GeneratedRecap } from '@/services/gameMasterApi'
import { useGame } from '@/providers'
import { useVoiceMode } from '@/hooks'

// Track which panels are expanded (for mobile accordion style)
type ExpandedPanel = 'status' | 'terminal' | 'portfolio' | 'history' | null

// Helper: Format location name for display
function formatLocation(location: string): string {
  const labels: Record<string, string> = {
    'trading-terminal': 'Trading Terminal',
    'apartment': 'Your Apartment',
    'penthouse-day': 'Penthouse',
    'penthouse-night': 'Penthouse',
    'yacht-exterior': 'Yacht Deck',
    'yacht-interior': 'Yacht Interior',
    'fed-building': 'Federal Building',
    'courthouse': 'Courthouse',
    'lambo-dealership': 'Exotic Car Dealership',
    'lambo-driving': 'On the Road',
    'singapore-marina': 'Singapore Marina',
    'dubai-downtown': 'Dubai Downtown',
    'miami-beach': 'Miami Beach',
    'beach-day': 'Paradise Beach',
    'conference-hall': 'Crypto Conference',
    'degen-den': 'The Degen Den',
    'hacker-house': 'Hacker House',
    'nightclub-vip': 'VIP Lounge',
    'rooftop-party': 'Rooftop Party',
    'coffee-shop': 'Coffee Shop',
  }
  return labels[location] || location.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Helper: Get time of day from game time
function getTimeOfDay(gameTime: { gameHour: number }): string {
  const hour = gameTime.gameHour
  if (hour >= 5 && hour < 12) return 'Morning'
  if (hour >= 12 && hour < 17) return 'Afternoon'
  if (hour >= 17 && hour < 21) return 'Evening'
  return 'Night'
}

// Microphone icon for input
const MicIcon: FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
)

// Mobile fixed input bar component with voice support
interface MobileInputBarProps {
  onSubmit: (action: string) => void
  disabled: boolean
  transcript: string
  isListening: boolean
  isSTTSupported: boolean
  onStartListening: () => void
  onStopListening: () => void
}

const MobileInputBar: FC<MobileInputBarProps> = ({
  onSubmit,
  disabled,
  transcript,
  isListening,
  isSTTSupported,
  onStartListening,
  onStopListening,
}) => {
  const [text, setText] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Update text when transcript changes (voice input)
  useEffect(() => {
    if (transcript) {
      setText(transcript)
    }
  }, [transcript])

  // Handle focus to ensure input is visible when keyboard opens
  const handleFocus = () => {
    // Small delay to let keyboard animate
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      // Also scroll the container into view
      if (containerRef.current) {
        containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }
    }, 300)
  }

  const handleSubmit = () => {
    if (text.trim() && !disabled) {
      onSubmit(text.trim())
      setText('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit()
    }
  }

  const handleMicClick = () => {
    if (isListening) {
      onStopListening()
    } else {
      setText('') // Clear text when starting voice input
      onStartListening()
    }
  }

  return (
    <div ref={containerRef} className="px-2 py-1.5 flex items-center gap-1">
      <span className="font-mono text-game-accent text-sm">&gt;</span>
      <input
        ref={inputRef}
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        placeholder={isListening ? "Listening..." : disabled ? "Loading..." : "What do you do?"}
        disabled={disabled}
        className={`flex-1 px-2 py-1 bg-game-bg border font-retro text-sm text-game-text placeholder:text-game-muted/60 focus:border-game-accent focus:outline-none disabled:opacity-50 ${
          isListening ? 'border-red-500 animate-pulse' : 'border-game-border'
        }`}
      />
      {isSTTSupported && (
        <button
          onClick={handleMicClick}
          disabled={disabled}
          className={`p-1 border transition-colors disabled:opacity-50 ${
            isListening
              ? 'bg-red-500/20 border-red-500 text-red-400'
              : 'bg-game-panel border-game-border text-game-muted hover:text-game-accent hover:border-game-accent'
          }`}
          title={isListening ? 'Stop listening' : 'Voice input'}
        >
          <MicIcon className={`w-5 h-5 ${isListening ? 'animate-pulse' : ''}`} />
        </button>
      )}
      <button
        onClick={handleSubmit}
        disabled={!text.trim() || disabled}
        className="px-3 py-1 font-pixel text-xs bg-game-accent text-game-bg border border-game-accent hover:bg-game-accent/80 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        GO
      </button>
    </div>
  )
}

export const GameScreen: FC = () => {
  const navigate = useNavigate()
  const { connected, isAuthenticated, fullDisconnect } = useWalletStore()
  const { session, isLoading, error } = useGameStore()
  const {
    abandonCurrentGame,
    currentSituation,
    currentOutcome,
    currentSceneImage,
    isGeneratingSituation,
    isGeneratingOutcome,
    makeCustomChoice,
    generateSituation,
  } = useGame()

  // Which panel is expanded on mobile (null = all minimized)
  const [expandedPanel, setExpandedPanel] = useState<ExpandedPanel>(null)

  // Onboarding modal state
  const [showOnboarding, setShowOnboarding] = useState(false)

  // Info dropdown
  const [showInfo, setShowInfo] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  // Share card state
  const [showShareCard, setShowShareCard] = useState(false)
  const [shareCardData, setShareCardData] = useState<ShareCardData | null>(null)

  // Win celebration state
  const [showWinCelebration, setShowWinCelebration] = useState(false)

  // Game recap state
  const [gameRecap, setGameRecap] = useState<GeneratedRecap | null>(null)
  const [isLoadingRecap, setIsLoadingRecap] = useState(false)

  // Pending trade state - intercepts trade intents before AI responds
  const [pendingTradeIntent, setPendingTradeIntent] = useState<TradeIntent | null>(null)
  const [pendingActionAfterTrade, setPendingActionAfterTrade] = useState<string | null>(null)

  // Voice mode
  const {
    isSpeaking,
    isListening,
    transcript,
    speak,
    stopSpeaking,
    startListening,
    startListeningForInput,
    stopListening,
    clearTranscript,
    isVoiceModeEnabled,
    isSTTSupported,
  } = useVoiceMode()

  // Track last spoken content to avoid repeating
  const lastSpokenRef = useRef<string | null>(null)

  // Auto-speak situation descriptions and outcomes
  useEffect(() => {
    if (!isVoiceModeEnabled || isGeneratingSituation || isGeneratingOutcome) return

    const textToSpeak = currentOutcome?.narrative || currentSituation?.description
    if (!textToSpeak) return

    // Don't repeat the same content
    if (lastSpokenRef.current === textToSpeak) return
    lastSpokenRef.current = textToSpeak

    // Add "What do you want to do?" prompt at the end for situations (not outcomes that end the game)
    const isGameOver = session?.status === 'won' || session?.status === 'lost'
    const fullText = isGameOver
      ? textToSpeak
      : `${textToSpeak}... What do you want to do?`

    // Wait a moment for typewriter to start, then speak
    const timer = setTimeout(() => {
      speak(fullText).catch(console.error)
    }, 300)

    return () => clearTimeout(timer)
  }, [currentSituation?.id, currentOutcome?.narrative, isVoiceModeEnabled, isGeneratingSituation, isGeneratingOutcome, speak, session?.status])

  // Handle voice input submission
  const handleVoiceInputComplete = useCallback(() => {
    if (transcript.trim()) {
      handleCustomAction(transcript.trim())
      clearTranscript()
    }
    stopListening()
  }, [transcript, clearTranscript, stopListening])

  // Auto-submit when listening stops and there's a transcript
  useEffect(() => {
    if (!isListening && transcript.trim() && isVoiceModeEnabled) {
      const timer = setTimeout(() => {
        handleVoiceInputComplete()
      }, 1000) // Wait 1 second after speech ends
      return () => clearTimeout(timer)
    }
  }, [isListening, transcript, isVoiceModeEnabled, handleVoiceInputComplete])

  // Track previous speaking state to detect when TTS finishes
  const wasSpeakingRef = useRef(false)

  // Auto-start listening after TTS finishes (voice mode hands-free flow)
  useEffect(() => {
    const wasJustSpeaking = wasSpeakingRef.current && !isSpeaking
    wasSpeakingRef.current = isSpeaking

    if (wasJustSpeaking && isVoiceModeEnabled && !isGeneratingSituation && !isGeneratingOutcome) {
      // Don't auto-listen if game is over
      const isGameOver = session?.status === 'won' || session?.status === 'lost'
      if (isGameOver) return

      // Small delay then start listening
      const timer = setTimeout(() => {
        startListening()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [isSpeaking, isVoiceModeEnabled, isGeneratingSituation, isGeneratingOutcome, session?.status, startListening])

  // Show onboarding when session first loads (for new games)
  useEffect(() => {
    if (session && shouldShowOnboarding()) {
      const timer = setTimeout(() => {
        setShowOnboarding(true)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [session?.id])

  // Show win celebration when game is won
  useEffect(() => {
    if (session?.status === 'won' && !showWinCelebration) {
      setShowWinCelebration(true)
    }
  }, [session?.status])

  // Generate recap when game ends
  useEffect(() => {
    if (!session || (session.status !== 'won' && session.status !== 'lost')) return
    if (gameRecap || isLoadingRecap) return // Already loaded or loading

    const generateRecap = async () => {
      setIsLoadingRecap(true)
      try {
        // Get recent actions from store for narrative context
        const { recentActions } = useGameStore.getState()

        // Determine end reason from the last action/outcome narrative
        let endReason = 'Unknown'
        if (session.status === 'won') {
          endReason = 'Hit the target goal'
        } else if (session.player.portfolio.totalValue <= 0) {
          endReason = 'Went bankrupt - portfolio reached $0'
        } else {
          // Use the most recent action's outcome as the end reason
          const lastAction = recentActions[0]
          if (lastAction?.outcome) {
            endReason = lastAction.outcome
          } else if (lastAction?.description) {
            endReason = 'Player action: ' + lastAction.description
          } else if (currentOutcome?.narrative) {
            endReason = currentOutcome.narrative
          } else {
            endReason = 'Game ended due to story consequences'
          }
        }

        // Build key events from recent player actions
        const keyEvents: string[] = recentActions
          .slice(0, 10)
          .reverse()
          .map(a => {
            if (a.outcome) return a.description + ' -> ' + a.outcome
            return a.description
          })
          .filter(Boolean)

        // Calculate trade stats (simplified)
        const trades = session.tradeHistory || []
        const sells = trades.filter(t => t.type === 'sell')
        const profitableTrades = sells.filter(t => {
          // Simplified P&L check
          return t.toAmount > t.fromAmount * t.price
        }).length
        const winRate = sells.length > 0 ? (profitableTrades / sells.length) * 100 : 0

        const recap = await generateGameRecapViaCloud(
          session.status as 'won' | 'lost',
          endReason,
          session.startingAmount,
          session.player.portfolio.totalValue,
          session.goal.targetAmount,
          session.decisionCount || 0,
          session.currentDay,
          keyEvents,
          trades.length > 0 ? `Traded ${trades[0]?.toToken || 'tokens'}` : 'No trades made',
          trades.length > 0 ? `Last trade: ${trades[trades.length - 1]?.type || 'unknown'}` : 'No trades made',
          winRate,
          session.player.reputation,
          session.player.traits.degenScore,
          session.player.traits.trustScore
        )
        setGameRecap(recap)
      } catch (error) {
        console.error('Failed to generate recap:', error)
        // Set a fallback recap
        setGameRecap({
          headline: session.status === 'won' ? 'WAGMI' : 'NGMI',
          summary: session.status === 'won'
            ? 'You hit your target and made it out of the trenches. Few can say the same.'
            : 'The trenches claimed another. The market is unforgiving, but there\'s always next time.',
          whatWentWell: 'You gave it your best shot.',
          whatWentWrong: session.status === 'won' ? 'Nothing major, you made it!' : 'The market had other plans.',
          deathBlow: session.status === 'lost' ? 'Your portfolio couldn\'t survive the trenches.' : null,
          finalQuote: session.status === 'won' ? 'Few understand.' : 'NGMI... for now.',
          rating: session.status === 'won' ? 'A (Diamond Hands)' : 'D (Paper Hands)',
        })
      } finally {
        setIsLoadingRecap(false)
      }
    }

    generateRecap()
  }, [session?.status, session?.id])

  // Redirect if no session or not connected
  useEffect(() => {
    if (!connected || !isAuthenticated) {
      navigate('/')
    }
  }, [connected, isAuthenticated, navigate])

  const handleMenuClick = () => {
    navigate('/')
  }

  const handleAbandon = async () => {
    if (window.confirm('Are you sure you want to abandon this game? Progress will be lost.')) {
      await abandonCurrentGame()
      navigate('/')
    }
  }

  // Handle custom action - intercepts trade intents before calling AI
  const handleCustomAction = async (action: string) => {
    // Check if this is a direct trade request
    if (isDirectTradeRequest(action)) {
      const intent = parseTradeIntent(action)
      if (intent) {
        // Store the action and show trade modal
        setPendingActionAfterTrade(action)
        setPendingTradeIntent(intent)
        return // Don't call AI yet - wait for trade execution
      }
    }

    // Not a trade request - proceed normally
    await makeCustomChoice(action)
  }

  // Handle trade completion - then continue with AI
  const handleTradeComplete = async (
    success: boolean,
    details?: { token: string; amount: number; type: 'buy' | 'sell' | 'swap' }
  ) => {
    const originalAction = pendingActionAfterTrade

    // Clear pending state
    setPendingTradeIntent(null)
    setPendingActionAfterTrade(null)

    if (!originalAction) return

    // Build a message that tells AI what actually happened
    let actionForAI: string
    if (success && details) {
      if (details.type === 'swap') {
        actionForAI = `[TRADE EXECUTED: SWAPPED ${details.amount.toLocaleString()} tokens into ${details.token}] ${originalAction}`
      } else {
        actionForAI = `[TRADE EXECUTED: ${details.type.toUpperCase()} ${details.amount.toLocaleString()} ${details.type === 'buy' ? 'USDC worth of' : ''} ${details.token}] ${originalAction}`
      }
    } else {
      actionForAI = `[TRADE SKIPPED - user decided not to trade] ${originalAction}`
    }

    // Now call AI with the actual result
    await makeCustomChoice(actionForAI)
  }

  // Handle trade cancel - user closed modal, pass action to AI as narrative
  const handleTradeCancelled = async () => {
    const originalAction = pendingActionAfterTrade
    setPendingTradeIntent(null)
    setPendingActionAfterTrade(null)

    // Pass the original action to the AI so the user's intent still plays through
    if (originalAction) {
      await makeCustomChoice(originalAction)
    }
  }

  const handleDisconnect = async () => {
    await fullDisconnect()
    navigate('/')
  }

  const handleSaveGame = () => {
    // Game auto-saves to Firebase, but show confirmation
    setSaveMessage('Game saved!')
    setTimeout(() => setSaveMessage(null), 2000)
    setShowInfo(false)
  }

  // Jump to panel section on mobile
  const handleJumpToPanel = () => {
    const panelSection = document.getElementById('game-panel-section')
    if (panelSection) {
      panelSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Handle opening terminal from portfolio bar
  const handleOpenTerminal = () => {
    setExpandedPanel('terminal')
  }

  // Handle opening portfolio from portfolio bar
  const handleOpenPortfolio = () => {
    setExpandedPanel('portfolio')
  }


  const handleShareGameOver = () => {
    if (!session) return

    const isWin = session.status === 'won'
    const multiplier = session.player.portfolio.totalValue / session.startingAmount

    // Generate a fitting message based on result
    const winMessages = [
      'From trenches to riches.',
      'Built different.',
      'Diamond hands paid off.',
      'The alpha was inside me all along.',
      'Few understand.',
    ]
    const loseMessages = [
      'The trenches claimed another.',
      'NGMI... this time.',
      'Back to the fiat mines.',
      'The market humbled me.',
      'Should have touched grass.',
    ]
    const messages = isWin ? winMessages : loseMessages
    const randomMessage = messages[Math.floor(Math.random() * messages.length)]

    const data: ShareCardData = {
      type: 'game_over',
      result: isWin ? 'win' : 'lose',
      startingAmount: session.startingAmount,
      endingAmount: session.player.portfolio.totalValue,
      multiplier,
      daysPlayed: session.currentDay,
      difficulty: session.difficulty,
      customMessage: randomMessage,
    }

    setShareCardData(data)
    setShowShareCard(true)
  }

  // Auto-generate first scene after onboarding closes (adventure style)
  useEffect(() => {
    if (session && !showOnboarding && !currentSituation && !currentOutcome && !isGeneratingSituation) {
      const isNewSession = !currentSituation && !currentOutcome
      if (isNewSession) {
        const timer = setTimeout(() => {
          console.log('Auto-generating opening scene...')
          // Start with "look around" to set the scene
          handleCustomAction('look around')
        }, 1000)
        return () => clearTimeout(timer)
      }
    }
  }, [showOnboarding, session?.id])

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RetroWindow title="NO ACTIVE GAME">
          <div className="text-center space-y-4">
            <PixelText color="muted">No active game session found</PixelText>
            {error && (
              <div className="p-2 bg-game-loss/20 border border-game-loss">
                <PixelText size="xs" color="loss">{error}</PixelText>
              </div>
            )}
            <RetroButton
              variant="primary"
              onClick={() => navigate('/')}
            >
              RETURN TO MENU
            </RetroButton>
          </div>
        </RetroWindow>
      </div>
    )
  }

  return (
    <div className="h-screen h-[100dvh] flex flex-col bg-game-bg">
      {/* Persistent Portfolio Bar - Always visible at top */}
      <PersistentPortfolioBar
        onOpenPortfolio={handleOpenPortfolio}
        onOpenTerminal={() => handleOpenTerminal()}
      />

      {/* Deadline Warning Toast */}
      <DeadlineWarning />

      {/* Pending Trade Modal - intercepts trade intents */}
      {pendingTradeIntent && (
        <PendingTradeModal
          isOpen={!!pendingTradeIntent}
          tradeIntent={pendingTradeIntent}
          onTradeComplete={handleTradeComplete}
          onCancel={handleTradeCancelled}
        />
      )}

      {/* Main Game Layout - fills remaining height */}
      <div className="flex-1 flex flex-col lg:flex-row gap-2 p-2 min-h-0 pb-2">
        {/* Left Column - Dynamic Scene + Situation - SCROLLABLE */}
        <div className="lg:flex-1 flex flex-col gap-2 min-w-0 overflow-y-auto min-h-0">
          {/* Dynamic Scene Viewport - only shown when an image is available */}
          {currentSceneImage?.url && (
            <div className="relative w-full aspect-video lg:aspect-[16/9] flex-shrink-0 border-2 border-game-border overflow-hidden">
              <img
                src={currentSceneImage.url}
                alt={currentSituation?.title || 'Scene'}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}

          {/* Scene Summary - Below image, shows location/time/characters */}
          <div className="flex-shrink-0 border-2 border-game-border bg-game-panel">
            <div className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* Location */}
              <div className="flex items-center gap-2">
                <span className="font-pixel text-xs text-game-muted">LOC:</span>
                <span className="font-retro text-sm text-game-accent">
                  {formatLocation(currentSituation?.location || session.world.currentLocation)}
                </span>
              </div>

              {/* Time */}
              <div className="flex items-center gap-2">
                <span className="font-pixel text-xs text-game-muted">TIME:</span>
                <span className="font-retro text-sm text-game-text">
                  Day {session.currentDay} • {getTimeOfDay(session.gameTime)}
                </span>
              </div>

              {/* Characters present - text only */}
              {currentSituation && currentSituation.characters.length > 0 && (
                <div className="flex items-center gap-1">
                  <span className="font-pixel text-xs text-game-muted">WITH:</span>
                  <span className="font-retro text-xs text-game-accent">
                    {currentSituation.characters.map(c => c.name).join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Voice Controls - Desktop only (mobile has own section) */}
          {isVoiceModeEnabled && (
            <div className="hidden lg:block flex-shrink-0 mb-2">
              <VoiceControls
                isSpeaking={isSpeaking}
                isListening={isListening}
                isVoiceModeEnabled={isVoiceModeEnabled}
                onStopSpeaking={stopSpeaking}
                onStartListening={startListening}
                onStopListening={stopListening}
                transcript={transcript}
              />
            </div>
          )}

          {/* Situation Panel - Adventure game input */}
          {/* mb-28 on mobile gives scroll space above fixed input bar + tabs */}
          <div className="flex-shrink-0 mb-28 lg:mb-0">
            <SituationPanel
              situation={currentSituation}
              outcome={currentOutcome}
              isLoading={isGeneratingSituation || isGeneratingOutcome}
              onCustomAction={handleCustomAction}
              onJumpToPanel={handleJumpToPanel}
              transcript={transcript}
              isListening={isListening}
              isSTTSupported={isSTTSupported}
              onStartListening={startListeningForInput}
              onStopListening={stopListening}
            />
          </div>
        </div>

        {/* MOBILE FIXED INPUT BAR - Above tabs, always visible */}
        <div className="lg:hidden fixed bottom-[44px] left-0 right-0 z-30 bg-game-bg border-t-2 border-game-border">
          {/* Voice Controls - shown above input when voice mode is enabled */}
          {isVoiceModeEnabled && (
            <div className="border-b border-game-border">
              <VoiceControls
                isSpeaking={isSpeaking}
                isListening={isListening}
                isVoiceModeEnabled={isVoiceModeEnabled}
                onStopSpeaking={stopSpeaking}
                onStartListening={startListening}
                onStopListening={stopListening}
                transcript={transcript}
              />
            </div>
          )}
          <MobileInputBar
            onSubmit={handleCustomAction}
            disabled={isGeneratingSituation || isGeneratingOutcome || !!pendingTradeIntent}
            transcript={transcript}
            isListening={isListening}
            isSTTSupported={isSTTSupported}
            onStartListening={startListeningForInput}
            onStopListening={stopListening}
          />
        </div>

        {/* Right Column - Tab Panel - FIXED at bottom on mobile, normal on desktop */}
        <div id="game-panel-section" className="fixed bottom-0 left-0 right-0 z-30 lg:relative lg:w-[420px] flex flex-col gap-2 min-h-0 flex-shrink-0 lg:flex-shrink lg:overflow-y-auto bg-game-bg lg:bg-transparent">
          {/* Tab Bar - All tabs + menu on one line */}
          <div className="border-2 border-game-border bg-game-panel flex flex-col-reverse lg:flex-col">
            {/* Tab Headers with Menu */}
            <div className="flex">
              <button
                onClick={() => setExpandedPanel(expandedPanel === 'status' ? null : 'status')}
                className={`flex-1 py-2 px-1 font-pixel text-xs sm:text-sm border-r border-game-border transition-colors ${
                  expandedPanel === 'status'
                    ? 'bg-game-accent/20 text-game-accent'
                    : 'bg-game-bg text-game-muted hover:bg-game-panel'
                }`}
              >
                STATUS
              </button>
              <button
                onClick={() => setExpandedPanel(expandedPanel === 'terminal' ? null : 'terminal')}
                className={`flex-1 py-2 px-1 font-pixel text-xs sm:text-sm border-r border-game-border transition-colors ${
                  expandedPanel === 'terminal'
                    ? 'bg-game-accent/20 text-game-accent'
                    : 'bg-game-bg text-game-muted hover:bg-game-panel'
                }`}
              >
                TERMINAL
              </button>
              <button
                onClick={() => setExpandedPanel(expandedPanel === 'portfolio' ? null : 'portfolio')}
                className={`flex-1 py-2 px-1 font-pixel text-xs sm:text-sm border-r border-game-border transition-colors ${
                  expandedPanel === 'portfolio'
                    ? 'bg-game-accent/20 text-game-accent'
                    : 'bg-game-bg text-game-muted hover:bg-game-panel'
                }`}
              >
                PORTFOLIO
              </button>
              <button
                onClick={() => setExpandedPanel(expandedPanel === 'history' ? null : 'history')}
                className={`flex-1 py-2 px-1 font-pixel text-xs sm:text-sm border-r border-game-border transition-colors ${
                  expandedPanel === 'history'
                    ? 'bg-game-accent/20 text-game-accent'
                    : 'bg-game-bg text-game-muted hover:bg-game-panel'
                }`}
              >
                HISTORY
              </button>
              {/* Menu button */}
              <div className="relative">
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className={`px-3 py-2 font-pixel text-xs sm:text-sm transition-colors ${
                    showInfo
                      ? 'bg-game-accent/20 text-game-accent'
                      : 'bg-game-bg text-game-muted hover:bg-game-panel'
                  }`}
                  title="Menu"
                >
                  ⚙
                </button>
                {/* Menu Dropdown */}
                {showInfo && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowInfo(false)}
                    />
                    <div className="absolute bottom-full lg:bottom-auto lg:top-full right-0 mb-1 lg:mb-0 lg:mt-1 w-48 bg-game-panel border-2 border-game-border z-50 shadow-lg">
                      <button
                        className="w-full text-left px-3 py-2 font-pixel text-sm text-game-text hover:bg-game-accent/20 hover:text-game-accent border-b border-game-border/50"
                        onClick={() => { handleMenuClick(); setShowInfo(false); }}
                      >
                        MAIN MENU
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 font-pixel text-sm text-game-text hover:bg-game-accent/20 hover:text-game-accent border-b border-game-border/50"
                        onClick={() => { handleSaveGame(); setShowInfo(false); }}
                      >
                        SAVE GAME
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 font-pixel text-sm text-game-loss hover:bg-game-loss/20 border-b border-game-border/50"
                        onClick={() => { handleAbandon(); setShowInfo(false); }}
                      >
                        QUIT GAME
                      </button>
                      <div className="px-3 py-2 border-b border-game-border/50">
                        <p className="font-retro text-xs text-game-muted">
                          Created by <a href="https://twitter.com/benhint" target="_blank" rel="noopener noreferrer" className="text-game-accent hover:underline">@benhint</a>
                        </p>
                        <p className="font-retro text-xs text-game-muted">
                          Tips: <span className="text-game-gold font-mono">benhint.skr</span>
                        </p>
                      </div>
                      <button
                        className="w-full text-left px-3 py-2 font-pixel text-sm text-game-text hover:bg-game-accent/20 hover:text-game-accent border-b border-game-border/50"
                        onClick={() => {
                          window.open('https://twitter.com/intent/tweet?text=Playing%20Trench%20Quest%20%F0%9F%8E%AE%20%40benhint', '_blank')
                          setShowInfo(false)
                        }}
                      >
                        SHARE
                      </button>
                      <button
                        className="w-full text-left px-3 py-2 font-pixel text-sm text-game-muted hover:bg-game-panel"
                        onClick={() => {
                          handleDisconnect()
                          setShowInfo(false)
                        }}
                      >
                        DISCONNECT
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Tab Content - Only show when a tab is selected */}
            {expandedPanel && (
              <div className="border-b lg:border-b-0 lg:border-t border-game-border">
                {/* Minimize/Close button */}
                <div className="flex justify-end p-1 border-b lg:border-b border-t lg:border-t-0 border-game-border/50 bg-game-bg/50 order-last lg:order-first">
                  <button
                    onClick={() => setExpandedPanel(null)}
                    className="px-2 py-0.5 font-pixel text-xs text-game-muted hover:text-game-accent hover:bg-game-panel transition-colors"
                    title="Close"
                  >
                    <span className="lg:hidden">▼ CLOSE</span>
                    <span className="hidden lg:inline">▲ MINIMIZE</span>
                  </button>
                </div>

                {/* STATUS Content - natural height, scroll only if needed */}
                {expandedPanel === 'status' && (
                  <div className="overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-300px)]">
                    <div className="p-2 border-b border-game-border/50">
                      <p className="font-pixel text-xs text-game-muted mb-2">MESSAGES</p>
                      <MessagePanel />
                    </div>
                    <div className="p-2">
                      <p className="font-pixel text-xs text-game-muted mb-2">MISSION</p>
                      <GameStatusPanel />
                    </div>
                  </div>
                )}

                {/* TERMINAL Content */}
                {expandedPanel === 'terminal' && (
                  <div className="overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-300px)]">
                    <TradingPanel />
                  </div>
                )}

                {/* PORTFOLIO Content */}
                {expandedPanel === 'portfolio' && (
                  <div className="overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-300px)]">
                    <PortfolioPanel />
                  </div>
                )}

                {/* HISTORY Content */}
                {expandedPanel === 'history' && (
                  <div className="overflow-y-auto max-h-[60vh] lg:max-h-[calc(100vh-300px)]">
                    <TradeHistoryPanel />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Onboarding Modal */}
      {showOnboarding && session && (
        <OnboardingModal
          session={session}
          onClose={() => setShowOnboarding(false)}
          onDontShowAgain={() => setShowOnboarding(false)}
        />
      )}

      {/* Win Celebration Overlay */}
      {showWinCelebration && session?.status === 'won' && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="text-center space-y-4 max-w-lg">
            {/* Confetti effect placeholder */}
            <div className="animate-bounce">
              <span className="text-5xl">&#127881;</span>
            </div>
            <PixelText size="xl" color="profit" className="text-3xl animate-pulse">
              {gameRecap?.headline || 'WAGMI!'}
            </PixelText>
            <PixelText size="lg" color="gold">
              YOU HIT YOUR TARGET!
            </PixelText>

            {/* Stats */}
            <div className="py-3 space-y-1">
              <p className="font-mono text-2xl text-game-profit">
                ${session.player.portfolio.totalValue.toLocaleString()}
              </p>
              <p className="font-retro text-lg text-game-muted">
                Target was ${session.goal.targetAmount.toLocaleString()}
              </p>
              <p className="font-pixel text-xl text-game-gold mt-2">
                {(session.player.portfolio.totalValue / session.startingAmount).toFixed(2)}x GAINS
              </p>
              {gameRecap?.rating && (
                <p className="font-pixel text-sm text-game-muted mt-1">
                  Rating: <span className="text-game-accent">{gameRecap.rating}</span>
                </p>
              )}
            </div>

            {/* AI Recap */}
            {isLoadingRecap ? (
              <div className="py-3">
                <div className="animate-spin w-5 h-5 border-2 border-game-accent border-t-transparent rounded-full mx-auto mb-2" />
                <p className="font-retro text-sm text-game-muted">Analyzing your legendary plays...</p>
              </div>
            ) : gameRecap ? (
              <div className="text-left space-y-2 p-3 border border-game-profit/30 bg-game-profit/5 rounded">
                <p className="font-retro text-sm text-game-text leading-relaxed">
                  {gameRecap.summary}
                </p>
                {gameRecap.whatWentWell && (
                  <p className="font-retro text-xs text-game-profit">
                    ✓ {gameRecap.whatWentWell}
                  </p>
                )}
              </div>
            ) : null}

            <p className="font-retro text-lg text-game-text italic pt-2">
              "{gameRecap?.finalQuote || 'Few understand the plays you made. Screenshot this, king.'}"
            </p>

            <div className="flex gap-4 justify-center mt-4">
              <RetroButton
                variant="primary"
                className="text-lg px-6 py-3"
                onClick={() => {
                  setShowWinCelebration(false)
                  handleShareGameOver()
                }}
              >
                SHARE WIN
              </RetroButton>
              <RetroButton
                variant="default"
                className="text-lg px-6 py-3"
                onClick={() => {
                  setShowWinCelebration(false)
                  navigate('/')
                }}
              >
                NEW GAME
              </RetroButton>
            </div>
          </div>
        </div>
      )}

      {/* Game Over Overlay (for losses) */}
      {session.status !== 'active' && session.status !== 'won' && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-auto">
          <RetroWindow
            title="GAME OVER"
            className="max-w-lg w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="text-center space-y-4">
              {/* Headline */}
              <PixelText size="lg" color="loss">
                {gameRecap?.headline || 'NGMI'}
              </PixelText>

              {/* Rating */}
              {gameRecap?.rating && (
                <p className="font-pixel text-sm text-game-muted">
                  Rating: <span className="text-game-accent">{gameRecap.rating}</span>
                </p>
              )}

              {/* Stats */}
              <div className="py-3 border-y border-game-border space-y-1 text-left">
                <div className="flex justify-between text-sm">
                  <span className="font-retro text-game-muted">Started with:</span>
                  <span className="font-mono text-game-text">
                    ${session.startingAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-retro text-game-muted">Ended with:</span>
                  <span className="font-mono text-game-loss">
                    ${session.player.portfolio.totalValue.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-retro text-game-muted">Target was:</span>
                  <span className="font-mono text-game-gold">
                    ${session.goal.targetAmount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="font-retro text-game-muted">Decisions made:</span>
                  <span className="font-mono text-game-text">
                    {session.decisionCount || 0}
                  </span>
                </div>
              </div>

              {/* Death narrative from last outcome */}
              {currentOutcome?.narrative && (
                <div className="text-left p-2 border border-game-loss/50 bg-game-loss/10">
                  <p className="font-pixel text-xs text-game-loss mb-1">WHAT KILLED YOU:</p>
                  <p className="font-retro text-sm text-game-text leading-relaxed">{currentOutcome.narrative}</p>
                </div>
              )}

              {/* AI Recap (additional analysis) */}
              {isLoadingRecap ? (
                <div className="py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-game-accent border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="font-retro text-sm text-game-muted">Analyzing your journey...</p>
                </div>
              ) : gameRecap ? (
                <div className="text-left space-y-3">
                  <p className="font-retro text-sm text-game-text leading-relaxed">
                    {gameRecap.summary}
                  </p>

                  {/* Final quote */}
                  <p className="font-retro text-sm text-game-muted italic text-center pt-2 border-t border-game-border">
                    "{gameRecap.finalQuote}"
                  </p>
                </div>
              ) : !currentOutcome?.narrative ? (
                <p className="font-retro text-sm text-game-muted italic">
                  "The market humbles everyone eventually. Touch grass and try again."
                </p>
              ) : null}

              <div className="flex gap-2 pt-2">
                <RetroButton
                  variant="primary"
                  className="flex-1"
                  onClick={async () => {
                    await abandonCurrentGame()
                    navigate('/')
                  }}
                >
                  TRY AGAIN
                </RetroButton>
                <RetroButton
                  variant="default"
                  className="flex-1"
                  onClick={handleShareGameOver}
                >
                  SHARE
                </RetroButton>
              </div>
            </div>
          </RetroWindow>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && !isGeneratingSituation && !isGeneratingOutcome && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <RetroWindow>
            <div className="flex items-center gap-3">
              <div className="animate-spin w-6 h-6 border-2 border-game-accent border-t-transparent rounded-full" />
              <PixelText>Processing...</PixelText>
            </div>
          </RetroWindow>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm">
          <RetroWindow title="ERROR" onClose={() => useGameStore.getState().setError(null)}>
            <PixelText color="loss" className="text-lg mb-3">{error}</PixelText>
            <div className="flex gap-2">
              <RetroButton
                size="sm"
                onClick={() => {
                  useGameStore.getState().setError(null)
                  generateSituation()
                }}
              >
                Retry
              </RetroButton>
              <RetroButton
                size="sm"
                variant="default"
                onClick={() => useGameStore.getState().setError(null)}
              >
                Dismiss
              </RetroButton>
            </div>
          </RetroWindow>
        </div>
      )}

      {/* Save Message Toast */}
      {saveMessage && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 bg-game-profit border-2 border-game-profit/50 shadow-lg">
          <span className="font-pixel text-base text-black">{saveMessage}</span>
        </div>
      )}

      {/* Share Card Preview Modal */}
      {showShareCard && shareCardData && (
        <ShareCardPreview
          data={shareCardData}
          onClose={() => {
            setShowShareCard(false)
            setShareCardData(null)
          }}
        />
      )}
    </div>
  )
}
